import React from "react";
import { Typography } from "@mui/material";
import { formatTitleCaseWords } from "@utils/formatUtils";
import { DASHBOARD } from "../dashboard/dashboardTheme";
import { formatFacilityDisplayName } from "@utils/facilityUtils";

export type NetworkFacilityLocation = {
  ccn?: string | null;
  provider_address?: string | null;
  address?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
};

export type NetworkFacilityNameInput = {
  provider_name?: unknown;
  ccn?: unknown;
  city?: unknown;
  state?: unknown;
  provider_address?: unknown;
  address?: unknown;
  address_1?: unknown;
};

export type CcnFacilityLocationMap = Record<string, NetworkFacilityLocation>;

function pickNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function lookupCcnLocation(
  ccn: string,
  locationByCcn?: CcnFacilityLocationMap,
): NetworkFacilityLocation | undefined {
  if (!locationByCcn || !ccn) return undefined;
  const trimmed = ccn.trim();
  if (locationByCcn[trimmed]) return locationByCcn[trimmed];
  if (/^\d+$/.test(trimmed)) {
    const padded = trimmed.padStart(6, "0");
    if (locationByCcn[padded]) return locationByCcn[padded];
    const unpadded = trimmed.replace(/^0+/, "") || "0";
    if (locationByCcn[unpadded]) return locationByCcn[unpadded];
  }
  for (const [key, value] of Object.entries(locationByCcn)) {
    if (key.replace(/^0+/, "") === trimmed.replace(/^0+/, "")) {
      return value;
    }
  }
  return undefined;
}

export function networkFacilityFieldsFromApi(
  row: Record<string, unknown>,
): NetworkFacilityNameInput {
  return {
    provider_name: row.provider_name,
    ccn: row.ccn,
    city: row.city,
    state: row.state,
    provider_address: row.provider_address,
    address: row.address,
    address_1: row.address_1,
  };
}

export function mergeNetworkFacilityFields(
  row: Record<string, unknown> | NetworkFacilityNameInput,
  locationByCcn?: CcnFacilityLocationMap,
  facilityNameToCcn?: Record<string, string>,
): NetworkFacilityNameInput {
  const base = networkFacilityFieldsFromApi(row as Record<string, unknown>);
  let ccn = pickNonEmptyString(base.ccn);
  if (!ccn && facilityNameToCcn) {
    const nameKey = pickNonEmptyString(base.provider_name).toLowerCase();
    if (nameKey) ccn = pickNonEmptyString(facilityNameToCcn[nameKey]);
  }
  const loc = lookupCcnLocation(ccn, locationByCcn);

  return {
    ...base,
    ccn: ccn || pickNonEmptyString(loc?.ccn),
    provider_address: pickNonEmptyString(
      base.provider_address,
      base.address,
      base.address_1,
      loc?.provider_address,
      loc?.address,
      loc?.address_1,
    ),
    city: pickNonEmptyString(base.city, loc?.city),
    state: pickNonEmptyString(base.state, loc?.state),
  };
}

export function formatNetworkFacilitySubline(
  facility: NetworkFacilityNameInput,
): string {
  const ccn = pickNonEmptyString(facility.ccn);
  const address = formatTitleCaseWords(
    pickNonEmptyString(
      facility.provider_address,
      facility.address,
      facility.address_1,
    ),
  );
  const city = formatTitleCaseWords(pickNonEmptyString(facility.city));
  const stateRaw = pickNonEmptyString(facility.state);
  const state = stateRaw
    ? /^[A-Za-z]{2}$/.test(stateRaw)
      ? stateRaw.toUpperCase()
      : formatTitleCaseWords(stateRaw)
    : "";
  const location = [address, city, state].filter(Boolean).join(", ");

  if (ccn && location) return `${ccn} · ${location}`;
  if (ccn) return ccn;
  return location || "—";
}

export default function NetworkFacilityNameCell({
  facility,
  locationByCcn,
  facilityNameToCcn,
}: {
  facility: NetworkFacilityNameInput | Record<string, unknown>;
  locationByCcn?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const merged = mergeNetworkFacilityFields(facility, locationByCcn, facilityNameToCcn);
  const rawName =
    merged.provider_name != null && String(merged.provider_name).trim()
      ? String(merged.provider_name)
      : merged.ccn != null
        ? String(merged.ccn)
        : "—";
  const name = rawName === "—" ? rawName : formatFacilityDisplayName(rawName);

  return (
    <>
      <Typography sx={{ fontWeight: 500, fontSize: "13px" }}>{name}</Typography>
      <Typography
        sx={{
          fontSize: "11.5px",
          color: DASHBOARD.textMuted,
          lineHeight: 1.35,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {formatNetworkFacilitySubline(merged)}
      </Typography>
    </>
  );
}
